import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { CustomCurrencyInput } from '../../../../../../app/components/CustomCurrencyInput';
import { useFormik } from 'formik';
import {
  formatBRLCurrencyMaskInput,
  getDigitsFromValue,
} from 'app/utils/currency';
import {
  TProducts,
  TypeProductEmission,
} from 'modules/products/context/ProductsProvider/products.interfaces';
import {
  CustomSelect,
  TCustomValue,
} from '../../../../../../app/components/CustomSelect';
import {
  YES_NO,
  SIMULATION_TYPES,
  TABS_CONTENT,
  TAC_TYPES,
  INTEREST_GRACE_PERIODS,
  AMORTIZATION_TYPES,
  TABS_KEYS,
  IOF_TYPES,
  SCHEDULE_TYPES_TRANSLATED_EN_PT,
  TABS_CONSIGNED_CONTENT,
  SIAPE_SIMULATION_TYPE,
  TABS_CONSIGNED_KEYS,
} from './constants';
import {
  getFormValidationSchema,
  getInterestBaseRateOptions,
  getInterestRateFrequencyOptions,
  getInterestTypeOptions,
  getValidDisbursementDate,
  getValueForSelect,
  getValueForSelectMulti,
  isPreInterestRateOption,
} from './helpers';
import CreditSimulatorFields from './CreditSimulatorFields';
import AmortizationGraceMonthsSelect from './AmortizationGraceMonthsSelect';
import LoanGuaranteeFieldColumn from '../components/LoanGuaranteeFieldColumn';
import { InputDropdown } from '../../../../../../app/components/InputDropdown';
import { TFilterItems } from '../../../../../../app/components/InputDropdown/InputDropdown';
import { CustomInput } from '../../../../../../app/components/CustomInput';
import { CalendarInput } from '../../../../../../app/components/CalendarInput';
import { formatDecimalField } from 'app/helpers';
import {
  InterestRateFrequency,
  InterestTypes,
  IofTypes,
  ISimulationResponse,
  PosFixedTypes,
  PreFixedTypes,
  ScheduleTypes,
  SimulationTypes,
  TacValueType,
  YesNoOptions,
} from './types';
import { TSimulatorV2Request } from 'app/services/simulatorHttpServiceV2';
import {
  formatCurrency,
  formatNumberToStringWithComma,
  formatToCurrencyFloat,
  formatToFixed,
} from 'app/utils/normalizer';
import { formatInterestRate } from 'modules/products/helpers/interestRate';
import { format } from 'date-fns';
import { useAppContext } from 'app/context/AppContextProvider';
import { ValidateBilletsResponse } from 'modules/products/interfaces/billets';
import { ValidateEmvResponse } from 'modules/products/interfaces/qrCode';
import { formatDateFromBackend } from 'app/helpers/dateHelpers';
import { CreditSimulatorFormContainer, StyledOptions } from './styles';
import { ApplicationsHttpService } from 'modules/products/services/api';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { useDebounce } from 'app/hooks';
import { notification } from 'antd';
import { ApplicationRequestContext } from 'modules/products/pages/ApplicationRequest/ApplicationRequest';
import { ConsigneeOrganType } from '../../ConsignedStep/interfaces';
import { TLoanGuaranteeFormValues } from '../../ConsultaFgts/interfaces';

export type TReactChangeInput = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

export type CreditSimulatorFormRefs = {
  simulate: () => void;
  reset: () => void;
  validateGuarantees: () => Promise<boolean>;
  getLoanGuaranteeFormValues: () => TLoanGuaranteeFormValues;
  destroyNotifications: () => void;
};

type CreditSimulatorFormProps = {
  tabIndex: string;
  product?: TProducts | null;
  simulationRequest?: TSimulatorV2Request | null;
  billetValidationResponse?: ValidateBilletsResponse | null;
  qrcodeEmvValidationResponse?: ValidateEmvResponse | null;
  onFormSuccess: (data: TSimulatorV2Request) => void;
  hasTimeValidation?: boolean;
  financedAmount?: number;
  onGuaranteeValidChange?: (isValid: boolean) => void;
  onCTFirstPaymentDateChange?: (filled: boolean) => void;
  firstPaymentDateError?: boolean;
  onClearFirstPaymentDateError?: () => void;
};

const CreditSimulatorForm = forwardRef<
  CreditSimulatorFormRefs,
  CreditSimulatorFormProps
>(
  (
    {
      tabIndex,
      product,
      simulationRequest,
      billetValidationResponse,
      qrcodeEmvValidationResponse,
      onFormSuccess,
      hasTimeValidation = false,
      financedAmount,
      onGuaranteeValidChange,
      onCTFirstPaymentDateChange,
      firstPaymentDateError,
      onClearFirstPaymentDateError,
    },
    ref,
  ) => {
    const { feature_flags } = useAppContext();
    const amountKey = product?.organ_type
      ? TABS_CONSIGNED_KEYS[Number(tabIndex)]
      : TABS_KEYS[Number(tabIndex)];
    const { showSnackbar } = useSnackbar();
    const [api, notificationContext] = notification.useNotification();
    const {
      setCobransaasResponse,
      consignedProduct: consigned,
      consignedConfig,
      fgtsPeriods,
      tacValidationError,
      setTacValidationError,
      loanGuaranteeInfo,
      loanGuaranteeFormValues,
      setLoanGuaranteeFormValues,
      simulationPersistedNumPayments,
      setSimulationPersistedNumPayments,
    } = useContext(ApplicationRequestContext);

    const [amountError, setAmountError] = useState('');
    const [consignedPayment, setConsignedPayment] = useState<number>();
    const [cobransaasList, setCobransaasList] =
      useState<ISimulationResponse[]>();
    const [insuranceValidationError, setInsuranceValidationError] =
      useState('');

    /* Variables */
    const isCt = product?.organ_type === ConsigneeOrganType.CT;
    const isConsignedMultiplesDates =
      isCt && product.multiple_disbursements_dates;
    const showRescissionPenaltyGuaranteeField =
      loanGuaranteeInfo?.available_rescission_allowance_percent != null;
    const showFgtsBalanceGuaranteeField =
      loanGuaranteeInfo?.available_fgts_guarantee_balance != null;
    const showFgtsRescissionPenaltyField =
      loanGuaranteeInfo?.severance_fine_fgts_guarantee != null;
    const tacMin =
      product?.tac_configuration?.type === TacValueType.PERCENTAGE
        ? product?.tac_configuration?.min * 100
        : Number(formatToCurrencyFloat(product?.tac_configuration?.min));
    const tacMax =
      product?.tac_configuration?.type === TacValueType.PERCENTAGE
        ? product?.tac_configuration?.max * 100
        : Number(formatToCurrencyFloat(product?.tac_configuration?.max));
    const maxAmountAllowed = product?.tac_configuration?.limit;
    const tacFixed = product?.tac_configuration?.fixed;
    const tacTypeProduct = product?.tac_configuration?.type;
    const shouldApplyTacFixed = tacFixed && tacFixed > 0;

    const insuranceMin = product?.insurance_configuration?.min;
    const insuranceMax = product?.insurance_configuration?.max;
    const insuranceFixed = product?.insurance_configuration?.fixed;
    const insuranceLimit = product?.insurance_configuration?.limit;
    const insuranceTypeProduct = product?.insurance_configuration?.type;
    const shouldApplyInsuranceFixed = insuranceFixed && insuranceFixed > 0;
    const isInsuranceValidationEnabled =
      (insuranceMin && insuranceMin > 0) ||
      (insuranceMax && insuranceMax > 0) ||
      (insuranceLimit && insuranceLimit > 0) ||
      (insuranceFixed && insuranceFixed > 0);

    const isTacValidationEnabled =
      (tacMin && tacMin > 0) ||
      (tacMax && tacMax > 0) ||
      (maxAmountAllowed && maxAmountAllowed > 0) ||
      (tacFixed && tacFixed > 0);

    const iofTypes = useMemo(() => {
      if (product?.iof_zero)
        return IOF_TYPES.filter((item) => item.value === IofTypes.ZERO);
      if (product?.borrower_type === 'PERSON')
        return IOF_TYPES.filter((item) => item.value === IofTypes.PERSON);
      if (product?.borrower_type === 'BUSINESS')
        return IOF_TYPES.filter((item) =>
          [IofTypes.BUSINESS, IofTypes.BUSINESS_SIMPLE].includes(item.value),
        );
      return IOF_TYPES;
    }, [product]);

    const tacRateTypes = useMemo(() => {
      if (amountKey === 'requestedAmount') return TAC_TYPES;
      return TAC_TYPES.filter(
        (item) => item.parameterName === TacValueType.MONETARY,
      );
    }, [amountKey]);

    const isDiscountRateVisible = useMemo(() => {
      return feature_flags?.DISCOUNT_RATE_ENABLED
        ? feature_flags?.DISCOUNT_RATE_ENABLED?.isActive
        : false;
    }, [feature_flags]);

    const availableRescissionAllowancePercent =
      loanGuaranteeInfo?.available_rescission_allowance_percent;

    const isFgts = product?.organ_type === ConsigneeOrganType.FGTS;
    const fgtsAmount: number =
      fgtsPeriods?.reduce((acc, p) => acc + p?.amount, 0) || 0;

    const isConsigned =
      product?.organ_type &&
      [ConsigneeOrganType.CT, ConsigneeOrganType.SIAPE].includes(
        product.organ_type,
      );
    const consignedAmount = consigned?.available_balance || 0;
    const consignedParcels = 96;
    const consignedTotalAmount = consignedAmount * consignedParcels;
    const hasConsignedAvailableBalance = consigned?.available_balance;

    const getInitialAmount = () => {
      if (
        product?.type_product_emission === TypeProductEmission.BILLET_PAYMENT
      ) {
        return (
          formatCurrency(billetValidationResponse?.total_value || 0, false) ??
          '0,00'
        );
      }
      if (product?.type_product_emission === TypeProductEmission.QRCODE_PIX) {
        return (
          formatCurrency(qrcodeEmvValidationResponse?.amount || 0, false) ??
          '0,00'
        );
      }
      return '0,00';
    };

    const cleanInitialValues = {
      amount: getInitialAmount(),
      hasAdditionalFee: null,
      simulationType: SimulationTypes.PRICE,
      interestType: InterestTypes.PRE,
      interestPreType: PreFixedTypes.BASE_360,
      interestRate: '1',
      interestPreRate: '1',
      interestPosType: PosFixedTypes.CDI,
      interestPosRate: '1',
      additionalInstallmentFee: '',
      iofType: iofTypes[0].value,
      scheduleType: product?.schedule_type ?? ScheduleTypes.MONTHLY,
      additionalInstallmentDescription: '',
      tacType: TacValueType.MONETARY,
      tacAmount: '0,00',
      financeFee: '0,00',
      insurance_amount: '0,00',
      numPayments: '1',
      discountRate: '0',
      firstPaymentDate: new Date(
        new Date().setMonth(new Date().getMonth() + 1),
      ).toISOString(),
      disbursementDate: getValidDisbursementDate(
        product?.multiple_disbursements_dates,
      ),
      disbursementDateStart: new Date().toISOString(),
      hasInterestMinGrace: null,
      interestGracePeriod: null,
      amortizationType: null,
      amortizationGraceMonths: [],
      financialIndicatorCobransaas: undefined,
      interestRateFrequency: InterestRateFrequency.MONTHLY,
      guarantee_balance_fgts_id: '',
      hasRescissionPenalty: false,
      rescissionPenaltyPercent: '',
      hasFgtsBalanceGuarantee: false,
      fgtsBalanceAmount: '',
      hasFgtsRescissionPenalty: false,
      fgtsRescissionPenaltyAmount: '',
    };

    const guaranteeFormInitialValues = useMemo(
      () => ({
        guarantee_balance_fgts_id:
          loanGuaranteeFormValues.guarantee_balance_fgts_id ?? '',
        hasRescissionPenalty: loanGuaranteeFormValues.hasRescissionPenalty,
        rescissionPenaltyPercent:
          loanGuaranteeFormValues.rescissionPenaltyPercent,
        hasFgtsBalanceGuarantee:
          loanGuaranteeFormValues.hasFgtsBalanceGuarantee,
        fgtsBalanceAmount: loanGuaranteeFormValues.fgtsBalanceAmount,
        hasFgtsRescissionPenalty:
          loanGuaranteeFormValues.hasFgtsRescissionPenalty,
        fgtsRescissionPenaltyAmount:
          loanGuaranteeFormValues.fgtsRescissionPenaltyAmount,
      }),
      [loanGuaranteeFormValues],
    );

    const persistedNumPayments = useMemo(
      () =>
        simulationPersistedNumPayments
          ? simulationPersistedNumPayments
          : undefined,
      [simulationPersistedNumPayments],
    );

    const initialValues = useMemo(() => {
      const getDisbursementDate = () => {
        if (hasTimeValidation) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(0, 0, 0, 0);
          return tomorrow.toISOString();
        }
        return getValidDisbursementDate(product?.multiple_disbursements_dates);
      };

      if (simulationRequest) {
        const tacType =
          typeof simulationRequest.tacRate === 'number'
            ? TacValueType.PERCENTAGE
            : TacValueType.MONETARY;
        const amount = simulationRequest[
          amountKey as keyof TSimulatorV2Request
        ] as number | undefined;
        const hasAdditionalFee =
          typeof simulationRequest.additionalInstallmentFee === 'number'
            ? YesNoOptions.YES
            : YesNoOptions.NO;

        return {
          amount: formatCurrency(amount, false),
          insurance_amount: formatCurrency(
            simulationRequest.insurance_amount,
            false,
          ),
          hasAdditionalFee,
          simulationType: SimulationTypes.PRICE,
          interestType: InterestTypes.PRE,
          interestPreType:
            simulationRequest?.interestPreType || PreFixedTypes.BASE_360,
          interestRate: String(
            formatToFixed(simulationRequest.interestRate * 100, 2),
          ).replace('.', ','),
          interestPreRate: String(
            formatToFixed(simulationRequest.interestRate * 100, 2),
          ).replace('.', ','),
          interestPosType: PosFixedTypes.CDI,
          interestPosRate: String(
            formatToFixed(simulationRequest.interestRate * 100, 2),
          ).replace('.', ','),
          additionalInstallmentFee: hasAdditionalFee
            ? formatCurrency(simulationRequest.additionalInstallmentFee!, false)
            : '',
          iofType: simulationRequest.iofType ?? iofTypes[0].value,
          scheduleType: product?.schedule_type ?? ScheduleTypes.MONTHLY,
          additionalInstallmentDescription:
            simulationRequest.additionalInstallmentDescription,
          tacType,
          tacAmount: formatCurrency(simulationRequest.tacAmount, false),
          financeFee: formatCurrency(simulationRequest.financeFee, false),
          numPayments:
            persistedNumPayments ?? String(simulationRequest.numPayments),
          discountRate:
            String(
              formatToFixed((simulationRequest.discountRate ?? 0) * 100, 2),
            ).replace('.', ',') || '0',
          firstPaymentDate: formatDateFromBackend(
            simulationRequest.firstPaymentDate as string,
          ).toISOString(),
          disbursementDate: formatDateFromBackend(
            simulationRequest.disbursementDate as string,
          ).toISOString(),
          disbursementDateStart: new Date().toISOString(),
          hasInterestMinGrace: null,
          interestGracePeriod: null,
          amortizationType: null,
          amortizationGraceMonths: [],
          financialIndicatorCobransaas: { label: '-', value: '-' },
          interestRateFrequency: isFgts
            ? InterestRateFrequency.ANNUALLY
            : simulationRequest.interestRateFrequency ||
              InterestRateFrequency.ANNUALLY,
          ...guaranteeFormInitialValues,
        };
      }
      return {
        ...cleanInitialValues,
        ...guaranteeFormInitialValues,
        ...(persistedNumPayments && { numPayments: persistedNumPayments }),
        disbursementDate: getDisbursementDate(),
        ...(isCt && {
          firstPaymentDate: null,
        }),
      };
    }, [
      simulationRequest,
      product,
      iofTypes,
      billetValidationResponse,
      qrcodeEmvValidationResponse,
      hasTimeValidation,
      guaranteeFormInitialValues,
      persistedNumPayments,
    ]);

    const validationSchema = useMemo(
      () =>
        getFormValidationSchema(
          product?.multiple_disbursements_dates,
          product?.organ_type,
        ),
      [product?.multiple_disbursements_dates, product?.organ_type],
    );

    const formik = useFormik({
      initialValues,
      validationSchema,
      onSubmit: async (values) => {
        const isPos = values.interestType === InterestTypes.POS;
        const rawValue = values?.amount;
        const numericValue = isFgts
          ? fgtsAmount
          : Number(rawValue?.replace(/\D/g, '')) / 100;

        if (tabIndex === '0' && consignedConfig) {
          if (consignedParcels < parseInt(values.numPayments)) {
            return showSnackbar(
              `Limite máximo de ${consignedParcels} parcelas`,
            );
          }

          if (
            consigned?.available_balance &&
            numericValue > consigned?.available_balance
          ) {
            return showSnackbar(
              `O valor da parcela tem que ser inferior ou igual a ${formatCurrency(
                consigned?.available_balance,
              )}`,
            );
          }
        }

        if (consignedConfig) {
          if (numericValue > consignedTotalAmount) {
            return showSnackbar(
              `O valor da parcela tem que ser inferior ou igual a ${formatCurrency(
                consignedTotalAmount,
              )}`,
            );
          }

          if (
            consignedPayment &&
            consigned?.available_balance &&
            numericValue / consignedPayment > consigned?.available_balance
          ) {
            return showSnackbar(
              'Valor indisponível. Aumente o número de parcelas',
            );
          }

          if (consignedParcels < parseInt(values.numPayments)) {
            return showSnackbar(
              `Limite máximo de ${consignedParcels} parcelas`,
            );
          }
        }

        const amount = Number(formatToCurrencyFloat(values.amount!));
        const numPayments = parseInt(values.numPayments);
        const tacAmount =
          values.tacType === TacValueType.MONETARY
            ? Number(formatToCurrencyFloat(values.tacAmount!))
            : undefined;
        const tacRate =
          values.tacType === TacValueType.PERCENTAGE
            ? formatInterestRate(values.tacAmount!)
            : undefined;
        const discountRate = formatInterestRate(values.discountRate) ?? 0;
        const interestRate = formatInterestRate(values.interestRate ?? '0')!;

        const financeFee = Number(formatToCurrencyFloat(values.financeFee!));
        const insuranceAmount = Number(
          formatToCurrencyFloat(values.insurance_amount!),
        );

        // Validação do seguro antes de submeter
        if (isInsuranceValidationEnabled && !shouldApplyInsuranceFixed) {
          let errorMessage = '';

          if (insuranceTypeProduct === TacValueType.PERCENTAGE) {
            const calculatedPercentage =
              amount > 0 ? (insuranceAmount / amount) * 100 : 0;

            if (insuranceMin && calculatedPercentage < insuranceMin) {
              errorMessage = `Insira um valor mínimo de ${insuranceMin}%`;
            } else if (insuranceMax && calculatedPercentage > insuranceMax) {
              errorMessage = `Insira um valor máximo de ${insuranceMax}%`;
            } else if (insuranceLimit && insuranceAmount > insuranceLimit) {
              errorMessage = `Valor limitado a ${formatCurrency(
                insuranceLimit,
              )}`;
            }
          } else if (insuranceTypeProduct === TacValueType.MONETARY) {
            if (insuranceLimit && insuranceAmount > insuranceLimit) {
              errorMessage = `Valor limitado a ${formatCurrency(
                insuranceLimit,
              )}`;
            } else if (insuranceMin && insuranceAmount < insuranceMin) {
              errorMessage = `Insira um valor mínimo de ${formatCurrency(
                insuranceMin,
              )}`;
            } else if (insuranceMax && insuranceAmount > insuranceMax) {
              errorMessage = `Insira um valor máximo de ${formatCurrency(
                insuranceMax,
              )}`;
            }
          }

          if (errorMessage) {
            setInsuranceValidationError(errorMessage);
            formik.setFieldTouched('insurance_amount', true);
            return;
          }
        }
        // Define o firstPaymentDate
        // Para FGTS, utiliza a menor data de antecipação selecionada (credit_date)
        // Para CT (Crédito do Trabalhador), o valor já vem como transfer_date_limit (yyyy-MM-dd)
        // Para os demais produtos, mantém o comportamento atual
        const firstPaymentDate =
          isFgts && fgtsPeriods?.length
            ? format(
                new Date(
                  fgtsPeriods
                    .map((p) => p.credit_date)
                    .sort(
                      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
                    )[0],
                ),
                'yyyy-MM-dd',
              )
            : isConsignedMultiplesDates
            ? values.firstPaymentDate!
            : format(new Date(values.firstPaymentDate!), 'yyyy-MM-dd');
        const disbursementDate = format(
          new Date(values.disbursementDate!),
          'yyyy-MM-dd',
        );

        const hasAdditionalFee = values.hasAdditionalFee === YesNoOptions.YES;
        const additionalInstallmentFee =
          values.additionalInstallmentFee && hasAdditionalFee
            ? Number(formatToCurrencyFloat(values.additionalInstallmentFee))
            : null;
        const additionalInstallmentDescription = hasAdditionalFee
          ? values.additionalInstallmentDescription
          : null;
        const iofType = values.iofType!;
        const scheduleType = values.scheduleType as string;
        const financialIndicatorCobransaas = isPos
          ? cobransaasList?.find(
              (index) =>
                index.id.toString() ===
                values.financialIndicatorCobransaas?.value,
            )
          : undefined;
        const gracePeriodAmortization = values?.amortizationGraceMonths.map(
          (item) => parseInt(item),
        );
        const interestPreType = values.interestPreType;
        const gracePeriodInterest =
          values.hasInterestMinGrace === 'true' && isPos
            ? parseInt(values?.interestGracePeriod || '0')
            : undefined;
        const interestRateFrequency = values.interestRateFrequency;

        onFormSuccess({
          [amountKey]: amount,
          balanceCheckId: consigned?.balance_check_id,
          numPayments,
          tacAmount,
          tacRate,
          discountRate,
          interestRate,
          financeFee,
          insurance_amount: insuranceAmount,
          interestPreType,
          firstPaymentDate,
          disbursementDate,
          additionalInstallmentFee,
          additionalInstallmentDescription,
          iofType,
          scheduleType,
          financialIndicatorCobransaas,
          simulationType: values?.simulationType || SimulationTypes.PRICE,
          amortizationFrequency:
            isPos && values?.amortizationType
              ? values?.amortizationType // MONTHLY, BIMONTHLY, SEMIANNUAL, ANNUAL
              : undefined,
          gracePeriodInterest,
          gracePeriodAmortization: isPos ? gracePeriodAmortization : undefined,
          interestRateFrequency,
        });

        return true;
      },
    });

    const validateTacValue = useCallback(
      (tacAmount: string, tacTypeFormik: string) => {
        const amountValue = tacAmount
          ? Number(formatToCurrencyFloat(tacAmount))
          : 0;
        const operationAmount = Number(
          formatToCurrencyFloat(formik.values?.amount || '0'),
        );

        if (shouldApplyTacFixed) {
          setTacValidationError('');
          return true;
        }

        if (!tacAmount || tacAmount === '0' || tacAmount === '0,00') {
          setTacValidationError('');
          return true;
        }

        if (!isTacValidationEnabled) {
          setTacValidationError('');
          return true;
        }

        if (tacTypeFormik === TacValueType.PERCENTAGE) {
          const percentage = parseFloat(tacAmount.replace(',', '.'));

          if ((tacTypeProduct as TacValueType) === TacValueType.PERCENTAGE) {
            if (tacMin && percentage < tacMin) {
              setTacValidationError(
                `Valor não permitido. Insira um valor mínimo de ${tacMin}%`,
              );
              return false;
            }

            if (tacMax && percentage > tacMax) {
              setTacValidationError(
                `Valor não permitido. Insira um valor máximo de ${tacMax}%`,
              );
              return false;
            }

            if (
              maxAmountAllowed &&
              parseFloat(((percentage / 100) * operationAmount).toFixed(2)) >
                maxAmountAllowed
            ) {
              setTacValidationError(
                `Valor não permitido. Valor limitado a ${formatCurrency(
                  maxAmountAllowed,
                )}`,
              );
              return false;
            }

            setTacValidationError('');
            return true;
          } else {
            const calculatedTacAmount = parseFloat(
              ((operationAmount * percentage) / 100).toFixed(2),
            );

            if (maxAmountAllowed && calculatedTacAmount > maxAmountAllowed) {
              setTacValidationError(
                `Valor não permitido. Valor limitado a ${formatCurrency(
                  maxAmountAllowed,
                )}`,
              );
              return false;
            }

            if (calculatedTacAmount > tacMax) {
              setTacValidationError(
                `Valor não permitido. Insira um valor máximo de ${formatCurrency(
                  tacMax,
                )}`,
              );
              return false;
            }

            if (calculatedTacAmount < tacMin) {
              setTacValidationError(
                `Valor não permitido. Insira um valor mínimo de ${formatCurrency(
                  tacMin,
                )}`,
              );
              return false;
            }
          }
        } else if (tacTypeFormik === TacValueType.MONETARY) {
          const tacAmountValue = Number(
            formatToCurrencyFloat(tacAmount || '0'),
          );

          if (maxAmountAllowed && tacAmountValue > maxAmountAllowed) {
            setTacValidationError(
              `Valor não permitido. Valor limitado a ${formatCurrency(
                maxAmountAllowed,
              )}`,
            );
            return false;
          }

          if (tacMin || tacMax) {
            if ((tacTypeProduct as TacValueType) === TacValueType.PERCENTAGE) {
              // Converte os limites de porcentagem para valores monetários
              const tacMinMonetary = tacMin
                ? parseFloat(((tacMin / 100) * operationAmount).toFixed(2))
                : null;
              const tacMaxMonetary = tacMax
                ? parseFloat(((tacMax / 100) * operationAmount).toFixed(2))
                : null;

              if (tacMinMonetary && tacAmountValue < tacMinMonetary) {
                setTacValidationError(
                  `Valor não permitido. Insira um valor mínimo de ${tacMin}%`,
                );
                return false;
              }

              if (tacMaxMonetary && tacAmountValue > tacMaxMonetary) {
                setTacValidationError(
                  `Valor não permitido. Insira um valor máximo de ${tacMax}%`,
                );
                return false;
              }

              setTacValidationError('');
              return true;
            } else {
              if (
                maxAmountAllowed &&
                amountValue &&
                amountValue > maxAmountAllowed
              ) {
                setTacValidationError(
                  `Valor não permitido. Valor limitado a ${formatCurrency(
                    maxAmountAllowed,
                  )}`,
                );
                return false;
              }

              if (tacMin && amountValue < tacMin) {
                setTacValidationError(
                  `Valor não permitido. Insira um valor mínimo de ${formatCurrency(
                    tacMin,
                  )}`,
                );
                return false;
              }

              if (tacMax && amountValue > tacMax) {
                setTacValidationError(
                  `Valor não permitido. Insira um valor máximo de ${formatCurrency(
                    tacMax,
                  )}`,
                );
                return false;
              }
            }
          }

          setTacValidationError('');
          return true;
        }

        setTacValidationError('');
        return true;
      },
      [
        tacMin,
        tacMax,
        tacTypeProduct,
        maxAmountAllowed,
        formik.values,
        isTacValidationEnabled,
      ],
    );

    const validateInsuranceValue = useCallback(
      (insuranceAmount: string) => {
        const insuranceAmountValue = insuranceAmount
          ? Number(formatToCurrencyFloat(insuranceAmount))
          : 0;
        const operationAmount = Number(
          formatToCurrencyFloat(formik.values?.amount || '0'),
        );

        if (!isInsuranceValidationEnabled) {
          setInsuranceValidationError('');
          return true;
        }

        if (shouldApplyInsuranceFixed) {
          setInsuranceValidationError('');
          return true;
        }

        if (
          !insuranceAmount ||
          insuranceAmount === '0' ||
          insuranceAmount === '0,00'
        ) {
          setInsuranceValidationError('');
          return true;
        }

        if (insuranceTypeProduct === TacValueType.PERCENTAGE) {
          const calculatedPercentage =
            operationAmount > 0
              ? (insuranceAmountValue / operationAmount) * 100
              : 0;

          if (insuranceMin && calculatedPercentage < insuranceMin) {
            setInsuranceValidationError(
              `Insira um valor mínimo de ${insuranceMin}%`,
            );
            return false;
          }

          if (insuranceMax && calculatedPercentage > insuranceMax) {
            setInsuranceValidationError(
              `Insira um valor máximo de ${insuranceMax}%`,
            );
            return false;
          }

          if (insuranceLimit && insuranceAmountValue > insuranceLimit) {
            setInsuranceValidationError(
              `Valor limitado a ${formatCurrency(insuranceLimit)}`,
            );
            return false;
          }

          setInsuranceValidationError('');
          return true;
        } else if (insuranceTypeProduct === TacValueType.MONETARY) {
          if (insuranceLimit && insuranceAmountValue > insuranceLimit) {
            setInsuranceValidationError(
              `Valor limitado a ${formatCurrency(insuranceLimit)}`,
            );
            return false;
          }

          if (insuranceMin && insuranceAmountValue < insuranceMin) {
            setInsuranceValidationError(
              `Insira um valor mínimo de ${formatCurrency(insuranceMin)}`,
            );
            return false;
          }

          if (insuranceMax && insuranceAmountValue > insuranceMax) {
            setInsuranceValidationError(
              `Insira um valor máximo de ${formatCurrency(insuranceMax)}`,
            );
            return false;
          }

          setInsuranceValidationError('');
          return true;
        }

        setInsuranceValidationError('');
        return true;
      },
      [
        insuranceMin,
        insuranceMax,
        insuranceLimit,
        insuranceTypeProduct,
        shouldApplyInsuranceFixed,
        formik.values,
        isInsuranceValidationEnabled,
      ],
    );

    // Validar TC sempre que os valores mudarem
    useEffect(() => {
      validateTacValue(
        formik.values.tacAmount || '',
        formik.values.tacType || '',
      );
    }, [formik.values.tacAmount, formik.values.tacType, validateTacValue]);

    // Validar Seguro sempre que os valores mudarem
    useEffect(() => {
      validateInsuranceValue(formik.values.insurance_amount || '');
    }, [
      formik.values.insurance_amount,
      formik.values.amount,
      validateInsuranceValue,
    ]);

    const validateGuarantees = (showErrors = true): boolean => {
      const guaranteeNotificationKey = 'guarantee-validation-error';

      const clearGuaranteeError = () => {
        if (showErrors) {
          api.destroy(guaranteeNotificationKey);
        }
      };

      const hasRescissionPenalty = formik.values.hasRescissionPenalty;
      const hasFgtsBalanceGuarantee = formik.values.hasFgtsBalanceGuarantee;
      const hasFgtsRescissionPenalty = formik.values.hasFgtsRescissionPenalty;

      if (
        !hasRescissionPenalty &&
        !hasFgtsBalanceGuarantee &&
        !hasFgtsRescissionPenalty
      ) {
        clearGuaranteeError();
        return true;
      }

      if (!financedAmount) {
        return true;
      }

      clearGuaranteeError();

      const expectedAmount = Math.round(financedAmount * 0.5 * 100) / 100;
      const formattedExpected = formatCurrency(expectedAmount);
      const margin = consigned?.available_balance ?? 0;
      const parseCurrencyValue = (value?: string) =>
        Number((value ?? '').replace(/\D/g, '') || 0) / 100;
      const fgtsBalanceValue = parseCurrencyValue(
        formik.values.fgtsBalanceAmount,
      );
      const fgtsMultaValue = parseCurrencyValue(
        formik.values.fgtsRescissionPenaltyAmount,
      );
      const isExactAmount = (amount: number) =>
        Math.round(amount * 100) / 100 === expectedAmount;

      const notifyError = (message: string) => {
        if (showErrors) {
          api.error({
            key: guaranteeNotificationKey,
            message,
            duration: 10000,
            pauseOnHover: true,
          });
        }
      };

      if (
        hasRescissionPenalty &&
        hasFgtsBalanceGuarantee &&
        hasFgtsRescissionPenalty
      ) {
        const sum =
          Math.round((margin + fgtsBalanceValue + fgtsMultaValue) * 100) / 100;

        if (!isExactAmount(sum)) {
          notifyError(
            'A soma das garantias informadas deve corresponder a 50% do valor do empréstimo.',
          );
          return false;
        }

        return true;
      }

      if (hasRescissionPenalty && hasFgtsBalanceGuarantee) {
        const sum = Math.round((margin + fgtsBalanceValue) * 100) / 100;

        if (!isExactAmount(sum)) {
          notifyError(
            'A soma das garantias informadas deve corresponder a 50% do valor do empréstimo.',
          );
          return false;
        }

        return true;
      }

      if (hasRescissionPenalty && hasFgtsRescissionPenalty) {
        const sum = Math.round((margin + fgtsMultaValue) * 100) / 100;

        if (!isExactAmount(sum)) {
          notifyError(
            'A soma das garantias informadas deve corresponder a 50% do valor do empréstimo.',
          );
          return false;
        }

        return true;
      }

      if (hasFgtsBalanceGuarantee && hasFgtsRescissionPenalty) {
        const sum = Math.round((fgtsBalanceValue + fgtsMultaValue) * 100) / 100;

        if (!isExactAmount(sum)) {
          notifyError(
            'A soma das garantias informadas deve corresponder a 50% do valor do empréstimo.',
          );
          return false;
        }

        return true;
      }

      if (hasRescissionPenalty) {
        if (Math.round(margin * 100) / 100 < expectedAmount) {
          notifyError(
            'O valor da margem precisa atingir ao menos 50% do valor do empréstimo.',
          );
          return false;
        }

        return true;
      }

      if (hasFgtsBalanceGuarantee) {
        if (!isExactAmount(fgtsBalanceValue)) {
          notifyError(
            `O valor do Saldo FGTS deve corresponder a 50% (${formattedExpected}) do valor do empréstimo.`,
          );
          return false;
        }

        return true;
      }

      if (hasFgtsRescissionPenalty) {
        if (!isExactAmount(fgtsMultaValue)) {
          notifyError(
            `O valor da Multa rescisória associada ao FGTS deve corresponder a 50% (${formattedExpected}) do valor do empréstimo.`,
          );
          return false;
        }

        return true;
      }

      return true;
    };

    const debouncedFgtsBalanceAmount = useDebounce(
      formik.values.fgtsBalanceAmount,
      500,
    );
    const debouncedFgtsRescissionPenaltyAmount = useDebounce(
      formik.values.fgtsRescissionPenaltyAmount,
      500,
    );

    useImperativeHandle(
      ref,
      () => ({
        simulate: () => {
          formik.validateForm().then((errors) => {
            if (Object.keys(errors).length > 0) {
              Object.keys(errors).forEach((field) => {
                formik.setFieldTouched(field, true, false);
              });
              return;
            }

            formik.submitForm();
          });
        },
        validateGuarantees: async () => validateGuarantees(true),
        destroyNotifications: () => api.destroy(),
        getLoanGuaranteeFormValues: (): TLoanGuaranteeFormValues => ({
          guarantee_balance_fgts_id:
            formik.values.guarantee_balance_fgts_id ?? '',
          hasRescissionPenalty: formik.values.hasRescissionPenalty,
          rescissionPenaltyPercent: formik.values.rescissionPenaltyPercent,
          hasFgtsBalanceGuarantee: formik.values.hasFgtsBalanceGuarantee,
          fgtsBalanceAmount: formik.values.fgtsBalanceAmount,
          hasFgtsRescissionPenalty: formik.values.hasFgtsRescissionPenalty,
          fgtsRescissionPenaltyAmount:
            formik.values.fgtsRescissionPenaltyAmount,
        }),
        reset: () => {
          const getDisbursementDate = () => {
            if (hasTimeValidation) {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(0, 0, 0, 0);
              return tomorrow.toISOString();
            }
            return getValidDisbursementDate(
              product?.multiple_disbursements_dates,
            );
          };
          formik.resetForm({
            values: {
              ...cleanInitialValues,
              disbursementDate: getDisbursementDate(),
              ...(isCt && {
                firstPaymentDate: null,
              }),
            },
          });
          if (isCt && onCTFirstPaymentDateChange) {
            onCTFirstPaymentDateChange(false);
          }
        },
      }),
      [
        formik,
        hasTimeValidation,
        product,
        onCTFirstPaymentDateChange,
        financedAmount,
        consigned?.available_balance,
      ],
    );

    useEffect(() => {
      const isValid = validateGuarantees(false);
      onGuaranteeValidChange?.(isValid);
    }, [
      financedAmount,
      consigned?.available_balance,
      formik.values.hasRescissionPenalty,
      formik.values.hasFgtsBalanceGuarantee,
      formik.values.hasFgtsRescissionPenalty,
      formik.values.fgtsBalanceAmount,
      formik.values.fgtsRescissionPenaltyAmount,
    ]);

    useEffect(() => {
      validateGuarantees(true);
    }, [
      financedAmount,
      consigned?.available_balance,
      formik.values.hasRescissionPenalty,
      formik.values.hasFgtsBalanceGuarantee,
      formik.values.hasFgtsRescissionPenalty,
      debouncedFgtsBalanceAmount,
      debouncedFgtsRescissionPenaltyAmount,
    ]);

    useEffect(() => {
      if (amountKey !== 'requestedAmount') {
        formik.setFieldValue('tacType', TacValueType.MONETARY);
      }
    }, [amountKey]);

    // Aplicar valor do tacFixed automaticamente quando existir
    useEffect(() => {
      if (shouldApplyTacFixed && tacFixed) {
        formik.setFieldValue('tacType', TacValueType.MONETARY);
        formik.setFieldValue('tacAmount', formatCurrency(tacFixed, false));
      }
    }, [shouldApplyTacFixed, tacFixed, formik.setFieldValue]);

    // Aplicar valor do insuranceFixed automaticamente quando existir
    useEffect(() => {
      if (shouldApplyInsuranceFixed && insuranceFixed) {
        formik.setFieldValue(
          'insurance_amount',
          formatCurrency(insuranceFixed, false),
        );
      }
    }, [shouldApplyInsuranceFixed, insuranceFixed, formik.setFieldValue]);

    useEffect(() => {
      if (consignedPayment && consigned && tabIndex !== '0' && !isFgts) {
        const rawValue = formik.values?.amount;
        const numericValue = Number(rawValue?.replace(/\D/g, '')) / 100;

        if (consigned && numericValue > consignedTotalAmount) {
          setAmountError(
            `O valor da parcela tem que ser inferior ou igual a ${formatCurrency(
              consignedTotalAmount,
            )}`,
          );
          return;
        }

        if (
          consigned?.available_balance &&
          numericValue / consignedPayment > consigned?.available_balance
        ) {
          setAmountError('Valor indisponível. Aumente o número de parcelas');
        } else {
          setAmountError('');
        }
      }
    }, [formik.values?.amount, consignedPayment]);

    const handleCurrencyChange = (evt: TReactChangeInput) => {
      formik.setFieldValue(
        evt.target.name,
        formatBRLCurrencyMaskInput(evt.target.value ?? ''),
      );
    };

    const getFinancialIndicatorsHandler = async (): Promise<
      ISimulationResponse[] | undefined
    > => {
      try {
        const applicationService = new ApplicationsHttpService();

        const res = await applicationService.getFinancialIndicators();
        return res.data;
      } catch (error) {
        showSnackbar('Ocorreu um erro. Tente novamente.');
      }
    };

    const getFinancialIndicators = async () => {
      const response: ISimulationResponse[] | undefined =
        await getFinancialIndicatorsHandler();

      if (response) {
        setCobransaasList(response);
      }
    };

    const handleChangeSelect = async (evt: {
      target: { name: string; value?: TCustomValue };
    }) => {
      const firstItemCobransaasList = cobransaasList
        ? cobransaasList[0]
        : ({} as ISimulationResponse);

      const firstItemCobransaasListValueSelect = {
        label: firstItemCobransaasList?.acronym || '-',
        value: firstItemCobransaasList?.id?.toString() || '-',
      };

      if (evt.target.name === 'financialIndicatorCobransaas') {
        formik.setFieldValue('financialIndicatorCobransaas', evt.target.value);

        return;
      }

      // Info: Chama o endpoint com listagem de juros pós fixado
      if (evt.target.value?.value === InterestTypes.POS) {
        formik.setFieldValue(
          'financialIndicatorCobransaas',
          Object.keys(formik.values?.financialIndicatorCobransaas || {})
            ?.length > 0
            ? formik.values?.financialIndicatorCobransaas
            : firstItemCobransaasListValueSelect,
        );
        formik.setFieldValue('interestPreType', PreFixedTypes.BASE_360);
        formik.setFieldValue(
          'interestRateFrequency',
          InterestRateFrequency.ANNUALLY,
        );
      }

      // Seta o primeiro índice ao alterar a opção para Flexível
      if (evt.target.value?.value === SimulationTypes.IRREGULAR) {
        formik.setFieldValue(
          'financialIndicatorCobransaas',
          firstItemCobransaasListValueSelect,
        );
        formik.setFieldValue('amortizationType', ScheduleTypes.MONTHLY);
        formik.setFieldValue('interestType', InterestTypes.POS);
        formik.setFieldValue('interestPreRate', null);
        formik.setFieldValue('interestPreType', PreFixedTypes.BASE_360);
        formik.setFieldValue(
          'interestRateFrequency',
          InterestRateFrequency.ANNUALLY,
        );
      }

      //reset: interestPreType, interestRateFrequency, interestType,
      if (evt.target.value?.value === SimulationTypes.PRICE) {
        handleChangeSimulationTypeDefaultValue();
      }

      if (evt.target.value?.value === SimulationTypes.SAC) {
        handleChangeSimulationTypeDefaultValue();
      }

      if (evt.target.value?.value === SimulationTypes.SAA) {
        handleChangeSimulationTypeDefaultValue();
      }

      formik.setFieldValue(evt.target.name, evt.target.value?.value);
    };

    const handleChangeSimulationTypeDefaultValue = () => {
      formik.setFieldValue('interestPreType', PreFixedTypes.BASE_360);
      formik.setFieldValue(
        'interestRateFrequency',
        InterestRateFrequency.MONTHLY,
      );
      formik.setFieldValue('interestType', InterestTypes.PRE);
    };

    const handleChangeSelectMulti = (evt: {
      target: { name: string; value?: TCustomValue[] };
    }) => {
      const newValues = (evt.target.value ?? [])
        .map((v) => v.value)
        .filter((v): v is string => Boolean(v));
      const previousValues = (formik.values.amortizationGraceMonths ??
        []) as string[];
      const added = newValues.filter((v) => !previousValues.includes(v));

      if (added.length > 0) {
        const maxAdded = Math.max(...added.map(Number));
        const filled = Array.from({ length: maxAdded }, (_, i) =>
          (i + 1).toString(),
        );
        const merged = Array.from(new Set([...newValues, ...filled])).sort(
          (a, b) => Number(a) - Number(b),
        );
        formik.setFieldValue(evt.target.name, merged);
        return;
      }

      formik.setFieldValue(evt.target.name, newValues);
    };

    const handleChangeInputDropdown =
      (fieldName: string) => (value: string) => {
        formik.setFieldValue(fieldName, value);
      };

    const handleChangeInputFilterDropdown = useCallback(
      (fieldName: string) => (value: TFilterItems) => {
        if (fieldName === 'interestPosType') {
          const foundCobransaasParameter = cobransaasList?.find(
            (item) => item.id.toString() === value.parameterName,
          );

          setCobransaasResponse(foundCobransaasParameter);

          formik.setFieldValue(
            'financialIndicatorCobransaas',
            foundCobransaasParameter,
          );

          formik.setFieldValue(fieldName, value.name);
          formik.setFieldValue('interestType', InterestTypes.POS);
          formik.setFieldValue('interestPreRate', 1);
          formik.setFieldValue('interestPreType', PreFixedTypes.BASE_365);
          return;
        }

        if (fieldName === 'interestPreType') {
          formik.setFieldValue(fieldName, value.parameterName);
          formik.setFieldValue('interestPosRate', 1);
          formik.setFieldValue('interestPreRate', '');
          formik.setFieldValue('interestPosType', null);
          return;
        }

        formik.setFieldValue(fieldName, value.parameterName);
      },
      [cobransaasList],
    );

    const handleChangeNumber = (evt: TReactChangeInput) => {
      formik.setFieldValue(
        evt.target.name,
        getDigitsFromValue(evt.target.value),
      );
    };

    const handleChangeDatePicker =
      (fieldName: string) => (date: Date | null) => {
        if (!date) {
          formik.setFieldValue(fieldName, null);
          if (
            fieldName === 'firstPaymentDate' &&
            isCt &&
            onCTFirstPaymentDateChange
          ) {
            onCTFirstPaymentDateChange(false);
            onClearFirstPaymentDateError?.();
          }
          return;
        }

        if (isNaN(date.getTime())) {
          formik.setFieldValue(fieldName, null);
          return;
        }

        try {
          formik.setFieldValue(fieldName, date.toISOString());
          if (
            fieldName === 'firstPaymentDate' &&
            isCt &&
            onCTFirstPaymentDateChange
          ) {
            onCTFirstPaymentDateChange(true);
            onClearFirstPaymentDateError?.();
          }
        } catch (error) {
          console.error('Erro ao converter data:', error);
          formik.setFieldValue(fieldName, null);
        }
      };

    const handlePercentageChange = (evt: TReactChangeInput) => {
      formik.setFieldValue(
        evt.target.name,
        formatDecimalField(evt.target.value),
      );
    };

    const handleGuaranteeToggle = (
      checkboxField:
        | 'hasRescissionPenalty'
        | 'hasFgtsBalanceGuarantee'
        | 'hasFgtsRescissionPenalty',
      valueField:
        | 'rescissionPenaltyPercent'
        | 'fgtsBalanceAmount'
        | 'fgtsRescissionPenaltyAmount',
      checked: boolean,
      emptyValue = '',
    ) => {
      formik.setFieldValue(checkboxField, checked);
      if (!checked) {
        formik.setFieldValue(valueField, emptyValue);
      }
    };

    const numPayments = formik.values.numPayments;
    const today = useMemo(() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    }, []);
    const amortizationGraceMonthsOptions = useMemo(() => {
      if (!numPayments) return [];
      return Array.from({ length: parseInt(numPayments) }, (item, inx) => ({
        value: (inx + 1).toString(),
        label: `${inx + 1}ª parcela`,
      }));
    }, [numPayments]);

    const isIrregularType =
      formik.values.simulationType === SimulationTypes.IRREGULAR;

    useEffect(() => {
      getFinancialIndicators();

      if (consigned && !simulationPersistedNumPayments) {
        if (isFgts) {
          formik.setFieldValue('numPayments', fgtsPeriods?.length);
        } else {
          formik.setFieldValue('numPayments', 96);
        }
      }

      if (isFgts) {
        formik.setFieldValue(
          'amount',
          formatNumberToStringWithComma(fgtsAmount),
        );
      }
    }, []);

    useEffect(() => {
      if (formik.values.numPayments) {
        setSimulationPersistedNumPayments(formik.values.numPayments);
      }
    }, [formik.values.numPayments, setSimulationPersistedNumPayments]);

    useEffect(() => {
      setLoanGuaranteeFormValues({
        guarantee_balance_fgts_id:
          loanGuaranteeInfo?.guarantee_balance_fgts_id ||
          formik.values.guarantee_balance_fgts_id ||
          '',
        hasRescissionPenalty: formik.values.hasRescissionPenalty,
        rescissionPenaltyPercent: formik.values.rescissionPenaltyPercent,
        hasFgtsBalanceGuarantee: formik.values.hasFgtsBalanceGuarantee,
        fgtsBalanceAmount: formik.values.fgtsBalanceAmount,
        hasFgtsRescissionPenalty: formik.values.hasFgtsRescissionPenalty,
        fgtsRescissionPenaltyAmount: formik.values.fgtsRescissionPenaltyAmount,
      });
    }, [
      loanGuaranteeInfo?.guarantee_balance_fgts_id,
      formik.values.guarantee_balance_fgts_id,
      formik.values.hasRescissionPenalty,
      formik.values.rescissionPenaltyPercent,
      formik.values.hasFgtsBalanceGuarantee,
      formik.values.fgtsBalanceAmount,
      formik.values.hasFgtsRescissionPenalty,
      formik.values.fgtsRescissionPenaltyAmount,
      setLoanGuaranteeFormValues,
    ]);

    useEffect(() => {
      if (!loanGuaranteeInfo) {
        return;
      }

      if (loanGuaranteeInfo.guarantee_balance_fgts_id) {
        formik.setFieldValue(
          'guarantee_balance_fgts_id',
          loanGuaranteeInfo.guarantee_balance_fgts_id,
        );
      }

      if (
        loanGuaranteeInfo.available_fgts_guarantee_balance != null &&
        !formik.values.fgtsBalanceAmount &&
        !loanGuaranteeFormValues.hasFgtsBalanceGuarantee
      ) {
        formik.setFieldValue(
          'fgtsBalanceAmount',
          formatCurrency(
            loanGuaranteeInfo.available_fgts_guarantee_balance,
            false,
          ),
        );
      }

      if (
        loanGuaranteeInfo.severance_fine_fgts_guarantee != null &&
        !formik.values.fgtsRescissionPenaltyAmount &&
        !loanGuaranteeFormValues.hasFgtsRescissionPenalty
      ) {
        formik.setFieldValue(
          'fgtsRescissionPenaltyAmount',
          formatCurrency(
            loanGuaranteeInfo.severance_fine_fgts_guarantee,
            false,
          ),
        );
      }

      if (
        availableRescissionAllowancePercent != null &&
        !formik.values.rescissionPenaltyPercent &&
        !loanGuaranteeFormValues.hasRescissionPenalty
      ) {
        formik.setFieldValue(
          'rescissionPenaltyPercent',
          String(formatToFixed(availableRescissionAllowancePercent, 2)).replace(
            '.',
            ',',
          ),
        );
      }
    }, [
      loanGuaranteeInfo,
      availableRescissionAllowancePercent,
      loanGuaranteeFormValues.hasFgtsBalanceGuarantee,
      loanGuaranteeFormValues.hasFgtsRescissionPenalty,
      loanGuaranteeFormValues.hasRescissionPenalty,
    ]);

    return (
      <>
        {notificationContext}
        <CreditSimulatorFormContainer>
          <CreditSimulatorFields title="Informações da solicitação">
            <CustomCurrencyInput
              labelValue={
                hasConsignedAvailableBalance
                  ? TABS_CONSIGNED_CONTENT[Number(tabIndex)]
                  : TABS_CONTENT[Number(tabIndex)]
              }
              value={formik.values.amount!}
              handleInputChange={handleCurrencyChange}
              name="amount"
              disabled={
                [
                  TypeProductEmission.BILLET_PAYMENT,
                  TypeProductEmission.QRCODE_PIX,
                ].includes(
                  product?.type_product_emission as TypeProductEmission,
                ) || isFgts
              }
              validationError={
                isConsigned
                  ? amountError
                  : formik.touched.amount
                  ? formik.errors.amount
                  : ''
              }
              information={
                isConsigned
                  ? `Valor máximo de ${formatCurrency(
                      tabIndex === '0'
                        ? consigned?.available_balance || 0
                        : consignedTotalAmount,
                    )}`
                  : undefined
              }
              onChange={(event) => {
                const rawValue = event.target.value;

                const numericValue = Number(rawValue.replace(/\D/g, '')) / 100;

                formik.setFieldValue(
                  'amount',
                  formatBRLCurrencyMaskInput(rawValue),
                );

                // Validate do consignado
                if (consigned) {
                  setAmountError('');

                  // TAB VALOR PARCELA
                  if (
                    tabIndex === '0' &&
                    consigned?.available_balance &&
                    numericValue > consigned?.available_balance
                  ) {
                    setAmountError(
                      `O valor da parcela tem que ser inferior ou igual a ${formatCurrency(
                        consigned.available_balance,
                      )}`,
                    );
                    return;
                  } else {
                    // TAB VALOR TOTAL
                    if (consigned && numericValue > consignedTotalAmount) {
                      setAmountError(
                        `O valor da parcela tem que ser inferior ou igual a ${formatCurrency(
                          consignedTotalAmount,
                        )}`,
                      );
                      return;
                    }
                  }
                }
              }}
            />
            {!hasConsignedAvailableBalance && !isFgts && (
              <CustomSelect
                labelValue="Valor adicional? (parcela)"
                value={getValueForSelect<string>(
                  YES_NO,
                  formik.values.hasAdditionalFee,
                )}
                options={YES_NO}
                handleInputChange={handleChangeSelect}
                name="hasAdditionalFee"
                placeholder="Selecionar"
                isClearable={false}
              />
            )}
          </CreditSimulatorFields>
          {formik.values.hasAdditionalFee === YesNoOptions.YES && (
            <StyledOptions>
              <CustomCurrencyInput
                labelValue="Valor adicional por parcela"
                name="additionalInstallmentFee"
                value={formik.values.additionalInstallmentFee!}
                handleInputChange={handleCurrencyChange}
                validationError={
                  formik.touched.additionalInstallmentFee
                    ? formik.errors.additionalInstallmentFee
                    : ''
                }
              />
              <CustomInput
                labelValue="Motivo"
                value={formik.values.additionalInstallmentDescription!}
                handleInputChange={formik.handleChange}
                name="additionalInstallmentDescription"
                width="66%"
                placeholder="Escreva aqui (80 caracteres)"
                validationError={
                  formik.touched.additionalInstallmentDescription
                    ? formik.errors.additionalInstallmentDescription
                    : ''
                }
              />
            </StyledOptions>
          )}
          <CreditSimulatorFields title="Tarifas, juros, taxas, etc. ">
            {!isConsigned && (
              <CustomSelect
                labelValue="Tipo de amortização"
                value={getValueForSelect<string>(
                  SIMULATION_TYPES,
                  formik.values.simulationType,
                )}
                options={consigned ? SIAPE_SIMULATION_TYPE : SIMULATION_TYPES}
                handleInputChange={handleChangeSelect}
                name="simulationType"
                placeholder="Selecionar"
                isClearable={false}
                disabled={isFgts}
              />
            )}

            {!isConsigned && (
              <CustomSelect
                labelValue="Modalidade de juros"
                value={getValueForSelect<string>(
                  getInterestTypeOptions(formik.values.simulationType),
                  formik.values.interestType,
                )}
                options={getInterestTypeOptions(formik.values.simulationType)}
                handleInputChange={handleChangeSelect}
                name="interestType"
                placeholder="Selecionar"
                isClearable={false}
                disabled={isFgts}
              />
            )}

            {!isPreInterestRateOption(
              formik.values.interestType,
              formik.values.simulationType,
            ) && (
              <CustomSelect
                labelValue="Indexador"
                value={
                  formik.values.financialIndicatorCobransaas != undefined &&
                  formik.values.financialIndicatorCobransaas?.value !== '-'
                    ? {
                        label:
                          formik.values.financialIndicatorCobransaas?.label,
                        value:
                          formik.values.financialIndicatorCobransaas?.value,
                      }
                    : cobransaasList && cobransaasList?.length > 0
                    ? {
                        label: cobransaasList[0].acronym || '-',
                        value: cobransaasList[0]?.id.toString() || '-',
                      }
                    : {
                        label: '-',
                        value: '-',
                      }
                }
                options={
                  cobransaasList
                    ? cobransaasList?.map((item) => ({
                        label: item.acronym,
                        value: item?.id.toString() || '-',
                      }))
                    : [
                        {
                          label: '-',
                          value: '-',
                        },
                      ]
                }
                handleInputChange={handleChangeSelect}
                name="financialIndicatorCobransaas"
                placeholder="Selecionar"
                isClearable={false}
              />
            )}
            <CustomSelect
              labelValue="Base de juros"
              value={getValueForSelect<string>(
                getInterestBaseRateOptions(formik.values.interestType),
                formik.values.interestPreType,
              )}
              options={getInterestBaseRateOptions(formik.values.interestType)}
              handleInputChange={handleChangeSelect}
              name="interestPreType"
              placeholder="Selecionar"
              isClearable={false}
              disabled={isFgts}
            />
            <CustomSelect
              labelValue="Periodicidade da taxa"
              value={getValueForSelect<string>(
                getInterestRateFrequencyOptions(),
                formik.values.interestRateFrequency,
              )}
              options={getInterestRateFrequencyOptions()}
              handleInputChange={handleChangeSelect}
              name="interestRateFrequency"
              placeholder="Selecionar"
              isClearable={false}
              disabled={isFgts}
            />
            <CustomInput
              labelValue="Taxa de juros (%)"
              value={formik.values.interestRate}
              handleInputChange={handlePercentageChange}
              name="interestRate"
              placeholder="%"
              validationError={
                formik.touched.interestRate ? formik.errors.interestRate : ''
              }
            />
            <InputDropdown
              items={
                shouldApplyTacFixed
                  ? [
                      TAC_TYPES.find(
                        (type) => type.parameterName === TacValueType.MONETARY,
                      )!,
                    ]
                  : tacRateTypes
              }
              handleInputChange={
                shouldApplyTacFixed
                  ? () => {}
                  : handleChangeInputDropdown('tacAmount')
              }
              label={`Taxa de cadastro ${
                formik.values.tacType === 'PERCENTAGE' ? '(%)' : ''
              }`}
              handleSelectFilterItem={
                shouldApplyTacFixed
                  ? () => {}
                  : handleChangeInputFilterDropdown('tacType')
              }
              inputValue={formik.values.tacAmount ?? ''}
              selectedFilter={formik.values.tacType ?? ''}
              validationError={
                tacValidationError ||
                (formik.touched.tacAmount ? formik.errors.tacAmount : '')
              }
              disabled={!!shouldApplyTacFixed}
            />
            {shouldApplyTacFixed && (
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}
              >
                Valor fixo aplicado conforme configuração do produto
              </div>
            )}
            <CustomCurrencyInput
              labelValue="Valor do seguro"
              name="insurance_amount"
              value={formik.values.insurance_amount!}
              handleInputChange={handleCurrencyChange}
              validationError={
                insuranceValidationError ||
                (formik.touched.insurance_amount
                  ? formik.errors.insurance_amount
                  : '')
              }
              disabled={!!shouldApplyInsuranceFixed}
            />
            {shouldApplyInsuranceFixed && (
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}
              >
                Valor fixo de seguro aplicado conforme configuração do produto
              </div>
            )}
            <CustomCurrencyInput
              labelValue="Outros custos (absoluto)"
              name="financeFee"
              value={formik.values.financeFee!}
              handleInputChange={handleCurrencyChange}
              validationError={
                formik.touched.financeFee ? formik.errors.financeFee : ''
              }
            />
            {isDiscountRateVisible && (
              <CustomInput
                labelValue="Taxa de desconto"
                value={formik.values.discountRate}
                handleInputChange={handlePercentageChange}
                name="discountRate"
                placeholder="%"
                validationError={
                  formik.touched.discountRate ? formik.errors.discountRate : ''
                }
              />
            )}
            {!isFgts && (
              <CustomSelect
                labelValue="Tipo de IOF"
                value={getValueForSelect<string>(
                  iofTypes,
                  formik.values.iofType,
                )}
                options={iofTypes}
                handleInputChange={handleChangeSelect}
                name="iofType"
                placeholder="Selecionar"
                isClearable={false}
                validationError={
                  formik.touched.iofType ? formik.errors.iofType : ''
                }
              />
            )}
          </CreditSimulatorFields>
          <CreditSimulatorFields title="Parcelas">
            <CustomInput
              labelValue="Prazo (parcelas)"
              value={
                isFgts
                  ? fgtsPeriods?.length.toString() || '0'
                  : formik.values.numPayments
              }
              handleInputChange={handleChangeNumber}
              name="numPayments"
              validationError={
                formik.touched.numPayments ? formik.errors.numPayments : ''
              }
              information={
                consigned && !isFgts
                  ? `Máximo de ${consignedParcels || 0} parcelas`
                  : undefined
              }
              onChange={(event) => {
                setConsignedPayment(parseInt(event.target.value));
                formik.setFieldValue('numPayments', event.target.value);
              }}
              disabled={isFgts}
            />
            {!isFgts &&
              (isCt ? (
                <CalendarInput
                  name="firstPaymentDate"
                  labelValue="Data do 1º pagamento *"
                  placeholder="dd/mm/aaaa"
                  value={formik.values.firstPaymentDate ?? null}
                  onChange={handleChangeDatePicker('firstPaymentDate')}
                  hasError={firstPaymentDateError}
                  validationError={
                    formik.touched.firstPaymentDate
                      ? formik.errors.firstPaymentDate
                      : ''
                  }
                  minDate={today}
                />
              ) : (
                <CalendarInput
                  name="firstPaymentDate"
                  labelValue="Data do 1º pagamento"
                  placeholder="dd/mm/aaaa"
                  value={formik.values.firstPaymentDate}
                  onChange={handleChangeDatePicker('firstPaymentDate')}
                  minDate={today}
                  validationError={
                    formik.touched.firstPaymentDate
                      ? formik.errors.firstPaymentDate
                      : ''
                  }
                />
              ))}
            {!product?.multiple_disbursements_dates && !isFgts && (
              <CalendarInput
                name="foundation_date"
                labelValue="Data de liberação do recurso"
                placeholder="dd/mm/aaaa"
                value={formik.values.disbursementDate}
                onChange={handleChangeDatePicker('disbursementDate')}
                validationError={
                  formik.touched.disbursementDate
                    ? formik.errors.disbursementDate
                    : ''
                }
                minDate={
                  hasTimeValidation
                    ? (() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        tomorrow.setHours(0, 0, 0, 0);
                        return tomorrow;
                      })()
                    : today
                }
              />
            )}
            {!isFgts && (
              <CustomInput
                labelValue="Periocidade dos vencimentos"
                value={
                  SCHEDULE_TYPES_TRANSLATED_EN_PT[formik.values.scheduleType!]
                }
                handleInputChange={formik.handleChange}
                name="scheduleType"
                readOnly
              />
            )}
          </CreditSimulatorFields>
          {isIrregularType && (
            <CreditSimulatorFields>
              <CustomSelect
                labelValue="Carência de juros"
                value={getValueForSelect<string>(
                  YES_NO,
                  formik.values.hasInterestMinGrace,
                )}
                options={YES_NO}
                handleInputChange={handleChangeSelect}
                name="hasInterestMinGrace"
                placeholder="Selecionar"
                isClearable={false}
              />
              {isIrregularType && (
                <CustomSelect
                  labelValue="Período de carência de juros"
                  value={getValueForSelect<string>(
                    INTEREST_GRACE_PERIODS,
                    formik.values.interestGracePeriod,
                  )}
                  options={INTEREST_GRACE_PERIODS}
                  handleInputChange={handleChangeSelect}
                  name="interestGracePeriod"
                  placeholder="Selecionar"
                  isClearable={false}
                />
              )}
              <br />
              <CustomSelect
                labelValue="Frequência de amortização"
                value={getValueForSelect<string>(
                  AMORTIZATION_TYPES,
                  formik.values.amortizationType,
                )}
                options={AMORTIZATION_TYPES}
                handleInputChange={handleChangeSelect}
                name="amortizationType"
                placeholder="Selecionar"
                isClearable={false}
              />
              <AmortizationGraceMonthsSelect
                labelValue="Meses com carência da amortização"
                value={getValueForSelectMulti<string>(
                  amortizationGraceMonthsOptions,
                  formik.values.amortizationGraceMonths,
                )}
                options={amortizationGraceMonthsOptions}
                handleInputChange={handleChangeSelectMulti}
                name="amortizationGraceMonths"
                placeholder="Selecionar"
              />
            </CreditSimulatorFields>
          )}
          {isCt && showRescissionPenaltyGuaranteeField && (
            <CreditSimulatorFields title="Garantias do empréstimo">
              {showRescissionPenaltyGuaranteeField && (
                <LoanGuaranteeFieldColumn
                  label="Verba rescisória"
                  checked={formik.values.hasRescissionPenalty}
                  onToggle={(checked) =>
                    handleGuaranteeToggle(
                      'hasRescissionPenalty',
                      'rescissionPenaltyPercent',
                      checked,
                    )
                  }
                >
                  <CustomInput
                    labelValue="Verba rescisória (%)"
                    value={formik.values.rescissionPenaltyPercent}
                    handleInputChange={handlePercentageChange}
                    name="rescissionPenaltyPercent"
                    placeholder="%"
                    endAdornment="%"
                    validationError=""
                  />
                </LoanGuaranteeFieldColumn>
              )}
              {showFgtsBalanceGuaranteeField && (
                <LoanGuaranteeFieldColumn
                  label="Saldo FGTS"
                  checked={formik.values.hasFgtsBalanceGuarantee}
                  onToggle={(checked) =>
                    handleGuaranteeToggle(
                      'hasFgtsBalanceGuarantee',
                      'fgtsBalanceAmount',
                      checked,
                      '0,00',
                    )
                  }
                >
                  <CustomCurrencyInput
                    labelValue="Saldo FGTS (R$)"
                    name="fgtsBalanceAmount"
                    value={formik.values.fgtsBalanceAmount}
                    handleInputChange={handleCurrencyChange}
                    validationError=""
                  />
                </LoanGuaranteeFieldColumn>
              )}
              {showFgtsRescissionPenaltyField && (
                <LoanGuaranteeFieldColumn
                  label="Multa rescisória associada ao FGTS"
                  checked={formik.values.hasFgtsRescissionPenalty}
                  onToggle={(checked) =>
                    handleGuaranteeToggle(
                      'hasFgtsRescissionPenalty',
                      'fgtsRescissionPenaltyAmount',
                      checked,
                      '0,00',
                    )
                  }
                >
                  <CustomCurrencyInput
                    labelValue="Multa rescisória (R$)"
                    name="fgtsRescissionPenaltyAmount"
                    value={formik.values.fgtsRescissionPenaltyAmount}
                    handleInputChange={handleCurrencyChange}
                    validationError=""
                  />
                </LoanGuaranteeFieldColumn>
              )}
            </CreditSimulatorFields>
          )}
          {product?.multiple_disbursements_dates && (
            <CreditSimulatorFields
              title="Múltiplas datas de desembolso"
              tooltip="A operação será desembolsada de acordo com a data de
                  assinatura da operação, que poderá ser entre a data de início
                  e data fim do desembolso."
            >
              <CalendarInput
                name="disbursement_date_start"
                labelValue="Data de início do desembolso"
                placeholder="dd/mm/aaaa"
                value={formik.values.disbursementDateStart}
                onChange={handleChangeDatePicker('disbursementDateStart')}
                disabled={isCt && product?.multiple_disbursements_dates}
                minDate={today}
              />
              <CalendarInput
                name="foundation_date"
                labelValue="Data fim do desembolso"
                placeholder="dd/mm/aaaa"
                value={formik.values.disbursementDate}
                onChange={handleChangeDatePicker('disbursementDate')}
                validationError={
                  formik.touched.disbursementDate
                    ? formik.errors.disbursementDate
                    : ''
                }
                minDate={
                  isCt && product?.multiple_disbursements_dates
                    ? (() => {
                        const minToday = new Date();
                        minToday.setHours(0, 0, 0, 0);
                        return minToday;
                      })()
                    : hasTimeValidation
                    ? (() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        tomorrow.setHours(0, 0, 0, 0);
                        return tomorrow;
                      })()
                    : (() => {
                        const minToday = new Date();
                        minToday.setHours(0, 0, 0, 0);
                        return minToday;
                      })()
                }
                maxDate={
                  isCt && product?.multiple_disbursements_dates
                    ? (() => {
                        const minToday = new Date();
                        minToday.setHours(0, 0, 0, 0);
                        const maxDate = new Date(minToday);
                        maxDate.setDate(maxDate.getDate() + 14);
                        return maxDate;
                      })()
                    : undefined
                }
              />
            </CreditSimulatorFields>
          )}
        </CreditSimulatorFormContainer>
      </>
    );
  },
);

CreditSimulatorForm.displayName = 'CreditSimulatorForm';
export default CreditSimulatorForm;
